import { capitalize } from '../strings/capitalize';
import { uncapitalize } from '../strings/uncapitalize';

/**
 * Takes a camel case string and converts it to kebab case.
 *
 * ```ts
 * tt.camelToKebabCase('wordWordWord');
 * // => 'word-word-word'
 * ```
 */
export function camelToKebabCase(s: string) {
  return s.replace(/([A-Z])/g, (_0, upper) => `-${upper}`.toLowerCase());
}

/**
 * Takes a kebab case string and converts it to camel case.
 *
 * ```ts
 * tt.kebabToCamelCase('word-word-word');
 * // => 'wordWordWord'
 * ```
 */
export function kebabToCamelCase(s: string) {
  return uncapitalize(s.split('-').reduce((s, w) => s + capitalize(w), ''));
}

/**
 * Takes a pascal case string and converts it to kebab case.
 *
 * ```ts
 * tt.pascalToKebabCase('WordWordWord');
 * // => 'word-word-word'
 * ```
 */
export function pascalToKebabCase(s: string) {
  return camelToKebabCase(uncapitalize(s));
}

/**
 * Takes a kebab case string and converts it to pascal case.
 *
 * ```ts
 * tt.kebabToPascalCase('word-word-word');
 * // => 'WordWordWord'
 * ```
 */
export function kebabToPascalCase(s: string) {
  return capitalize(kebabToCamelCase(s));
}

/**
 * Takes a snake case string and converts it to kebab case.
 *
 * ```ts
 * tt.snakeToKebabCase('word_word_word');
 * // => 'word-word-word'
 * ```
 */
export function snakeToKebabCase(s: string) {
  return s.replace(/_/g, '-');
}

/**
 * Takes a kebab case string and converts it to snake case.
 *
 * ```ts
 * tt.kebabToSnakeCase('word-word-word');
 * // => 'word_word_word'
 * ```
 */
export function kebabToSnakeCase(s: string) {
  return s.replace(/-/g, '_');
}
